import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import api from '../api';
import { formatFen } from '../utils/money';

// 工人月度对账单（v2.2）：大字号、可打印、带双签字栏。
// 用途：月度结算时打印给工人核对签字，或直接把屏幕给工人看。
export default function WorkerStatement({ workerId, month }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [salaryRes, balRes] = await Promise.all([
          api.get(`/api/reports/salary?month=${month}`),
          api.get('/api/reports/wage-balance'),
        ]);
        const row = (salaryRes.data || []).find((r) => r.workerId === workerId);
        const bal = (balRes.data?.workers || []).find((w) => w.workerId === workerId);
        if (!row && !bal) {
          setError('该工人本月无工资数据');
          return;
        }
        setData({ row, bal: bal || { totalPayable: 0, totalPaid: 0, balance: 0 } });
      } catch (e) {
        setError(e.response?.data?.error || '对账单加载失败');
      }
    })();
  }, [workerId, month]);

  if (error) return <Box p={4}><Typography color="error">{error}</Typography></Box>;
  if (!data) return <Box p={8} display="flex" justifyContent="center"><CircularProgress /></Box>;

  const { row, bal } = data;
  const workerName = row?.workerName || '工人';
  const rows = [
    ['出勤天数', row ? `${row.workDays.toFixed(1)} 天` : '0 天'],
    ['记工工资', formatFen(row?.workAmount || 0)],
    ['加班工资', formatFen(row?.overtimeAmount || 0)],
    ['饭补', formatFen(row?.mealAllowanceTotal || 0)],
    ['补贴', formatFen(row?.subsidyAmount || 0)],
    ['借支（已扣）', `- ${formatFen(row?.advanceAmount || 0)}`],
    ['扣款（已扣）', `- ${formatFen(row?.deductionAmount || 0)}`],
  ];

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Box display="none" sx={{ '@media print': { display: 'block' } }} />
      <Box display={{ xs: 'flex', md: 'flex' }} justifyContent="space-between" alignItems="center" mb={3} sx={{ '@media print': { display: 'none' } }}>
        <Typography variant="h5">工人月度对账单</Typography>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          打印 / 存 PDF
        </Button>
      </Box>

      {/* 打印主体：大字号，黑白友好 */}
      <Box sx={{
        border: '2px solid #000',
        p: { xs: 3, md: 5 },
        '@media print': { border: 'none', p: 0 },
      }}>
        <Typography variant="h4" align="center" fontWeight={900} gutterBottom>
          工人工资对账单
        </Typography>
        <Typography align="center" sx={{ fontSize: 22, mb: 4 }}>
          {workerName} · {month}
        </Typography>

        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.2, borderBottom: '1px dashed #999' }}>
            <Typography sx={{ fontSize: 22 }}>{label}</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 600 }}>{value}</Typography>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 2.5, mt: 2, bgcolor: '#f0f0f0', px: 2, '@media print': { bgcolor: 'none', border: '2px solid #000' } }}>
          <Typography sx={{ fontSize: 26, fontWeight: 900 }}>本月应发工资</Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 900 }}>{formatFen(row?.totalAmount || 0)}</Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 1 }}>累计情况（截至打印日）</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
            <Typography sx={{ fontSize: 20 }}>累计应付工资</Typography>
            <Typography sx={{ fontSize: 20 }}>{formatFen(bal.totalPayable)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
            <Typography sx={{ fontSize: 20 }}>累计已付</Typography>
            <Typography sx={{ fontSize: 20 }}>{formatFen(bal.totalPaid)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>结余（正数=尚欠）</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 900 }}>{formatFen(bal.balance)}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 8, mb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 18 }}>工人签字：＿＿＿＿＿＿＿＿</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 18 }}>日期：＿＿＿＿＿＿＿＿</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 18 }}>负责人签字：＿＿＿＿＿＿＿＿</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 18 }}>日期：＿＿＿＿＿＿＿＿</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}


// 路由包装：/workers/:workerId/statement/:month
export function WorkerStatementWithParams() {
  const { workerId, month } = useParams();
  return <WorkerStatement workerId={workerId} month={month} />;
}
